// BEVÉTELEK — a Pénzügy lap második füle: LISTA, a Kiadások fül párja.
//
// Ugyanaz az alak, mint a kiadás-listán (közös `ListToolbar` + `DataTable` + összegző-csík): egy sor = egy
// bevétel-tétel a kiválasztott hónapra. A KIMUTATÁS (kártyák, chartok, drilldown) az ÖSSZEGZÉS fülön él —
// ez a fül a tételeket mutatja, nem az aggregátumot.
//
// ── EREDMÉNYSZEMLÉLET, NEM PÉNZFORGALOM ────────────────────────────────────────────────────────────────────
//
// A sor ahhoz a hónaphoz tartozik, amikor a szolgáltatás FUT — nem amikor a számla kiáll vagy a pénz
// beérkezik. Egy augusztusi szolgáltatás szeptemberben kiállított (utólagos) számlája tehát AUGUSZTUS sora.
// A periódus-díj a periódus hónapjaira terül szét: egy éves díj tizenketted részekben jelenik meg.
//
// ── A KÉT FORRÁS (a BFF dönti el, a lap kimondja) ──────────────────────────────────────────────────────────
//
// MÚLT hónap (ha lezárult) → a sorok a KIÁLLÍTOTT BIZONYLATOKBÓL (tény, számlaszámmal és linkkel);
// a JELEN és a JÖVŐ → a HOZZÁRENDELÉSEKBŐL számolva (terv). Egy még le nem generált múlt hónap is TERV-et
// mutat — üres lista helyett, ami azt hazudná, hogy nem volt bevétel.
import type { RevenueRowSchema } from '@atlas/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { AttentionBanner } from '@/components/shared/attention-banner';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander, type DetailsRow } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import { SummaryCard, SummaryCards, SummaryMetaRow } from '@/components/shared/summary-cards';
import { Divider } from '@/components/ui/divider';
import { Text } from '@/components/ui/text';
import type { FinanceView, FinanceViewProps } from '@/features/finance/view';
import { revenueForecastKeys } from '@/features/revenue-forecast/api';
import {
  isNonDefaultStatus,
  useRevenueFilters,
} from '@/features/revenue-forecast/use-revenue-filters';
import { api } from '@/lib/api';
import { isForbiddenStatus } from '@/lib/error-status';
import { formatMoney } from '@/lib/format-money';
import { formatMonthLabel } from '@/lib/month';
import { usePermissions } from '@/lib/use-permissions';

type RevenueRow = z.infer<typeof RevenueRowSchema>;

const PAGE_SIZE = 100;

/** A sor gyakoriság-címkéje (eseti tételnél az „eseti", nem a frekvencia). */
function frequencyLabel(row: RevenueRow, t: TFunction): string | null {
  if (row.billingType === 'one_time') return t('revenueForecast.frequencyLabel.one_time');
  if (row.billingFrequency === null) return null;
  return t(`revenueForecast.frequencyLabel.${row.billingFrequency}`, {
    defaultValue: row.billingFrequency,
  });
}

export function useRevenueListView({
  enabled,
  month,
  onMonthChange,
}: FinanceViewProps): FinanceView {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const canSeeInvoices = can('invoices.view');
  const locale = i18n.language;
  const { filterItems, searchInput, setSearchInput, filters, query, hasNarrowing } =
    useRevenueFilters({ enabled, month });

  const listKey = useMemo(() => revenueForecastKeys.rows(filters), [filters]);
  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled,
    // A LEKÉRÉS DRÁGA (a BFF teljes-listás Odin-olvasásokból építi a hónapot), ezért ugyanaz az ÖT PERCES
    // frissesség, mint a kimutatáson: a hónapok közti oda-vissza lépkedés nem terheli újra a buszt.
    staleTime: 5 * 60 * 1000,
    queryFn: ({ pageParam }) =>
      api.revenueRows.query({ query: { ...query, limit: PAGE_SIZE, offset: pageParam } }),
    initialPageParam: 0,
    getNextPageParam: (last) =>
      last.status === 200 ? (last.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(() => pages.flatMap((p) => (p.status === 200 ? p.body.rows : [])), [pages]);
  const head = pages.find((p) => p.status === 200);
  const body = head?.status === 200 ? head.body : undefined;
  // a hónap az ADAT-HORIZONT előtt van → nincs Atlas-számla (a lista a tervet mutatja, és ez nem hiba)
  // `'dataHorizon' in body`: a telepítési ablakban egy régebbi BFF-válaszból hiányozhat
  const horizonFrom = body && 'dataHorizon' in body ? body.dataHorizon.actualsFrom : null;
  const beforeHorizon = horizonFrom !== null && month < horizonFrom;
  const isForbidden = pages.some((p) => isForbiddenStatus(p.status));
  const isError = infinite.isError || (pages.length > 0 && !pages.some((p) => p.status === 200));
  const loadMore = useCallback(() => {
    if (infinite.hasNextPage && !infinite.isFetchingNextPage) void infinite.fetchNextPage();
  }, [infinite]);

  /**
   * A „Részletek" kibontó tartalma — a kiadás-lap mintája: ami a SORBAN nem fér el, de a sorról szól.
   *
   * Ide költözött a GYAKORISÁG („havi") és a STÁTUSZ is: mindkettő a hozzárendelés tulajdonsága, nem
   * önálló lista-dimenzió, és külön oszlopban csak keskeny hasábot foglalt. A BIZONYLAT-SZÁM itt LINK is,
   * ha a hívónak van jog a számla-modulhoz — a „honnan jön ez a szám" kérdésre egy kattintás a válasz.
   */
  const detailRows = (row: RevenueRow): DetailsRow[] => {
    const out: DetailsRow[] = [];
    // A FIÓK NEM ISMÉTLŐDIK ITT: a sor fejében, az ügyfél alatt már ott áll (user-lelet) — a kibontó
    // ugyanazt mondta el másodszor. A helyére a SZÁMLÁZÁSI NÉV került: az a sorból nem olvasható ki, és
    // pont azt mondja meg, kinek a nevére megy a bizonylat (a fiók-saját számlázás ettől eltérhet az
    // ügyfél nevétől).
    if (row.billingName)
      out.push({ label: t('revenueForecast.columns.billingName'), value: row.billingName });
    if (row.planName) out.push({ label: t('revenueForecast.columns.plan'), value: row.planName });
    const frequency = frequencyLabel(row, t);
    if (frequency !== null) {
      out.push({ label: t('revenueForecast.columns.frequency'), value: frequency });
    }
    if (row.status !== null) {
      out.push({
        label: t('revenueForecast.columns.status'),
        // KISBETŰS a kibontóban: itt a státusz nem CÍMKE (jelvény/szűrő-érték), hanem a „Státusz: …" mondat
        // folytatása — nagy kezdőbetűvel mondat közepén állna. A kisbetűsítés CSS-sel megy, hogy az i18n-kulcs
        // EGY maradjon (ugyanaz a szöveg jelvényként nagybetűs) — ugyanaz a minta, mint a szűrő-sáv „mind"-je.
        value: (
          <span className="lowercase">
            {t(`revenueForecast.status.${row.status}`, { defaultValue: row.status })}
          </span>
        ),
      });
    }
    if (row.invoiceNumber !== null) {
      out.push({
        label: t('revenueForecast.columns.invoice'),
        value:
          row.invoiceId !== null && canSeeInvoices ? (
            <Link
              to="/invoices/$id"
              params={{ id: row.invoiceId }}
              className="text-subtle hover:text-foreground truncate tabular-nums underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              {row.invoiceNumber}
            </Link>
          ) : (
            // Jog nélkül a SZÁM akkor is látszik (az adat nem titok), csak a részlet-lap nem nyílik meg.
            <span className="tabular-nums">{row.invoiceNumber}</span>
          ),
      });
    }
    return out;
  };

  const columns: DataTableColumn<RevenueRow>[] = [
    {
      // 1. PARTNER — a lista vezető azonosítója (a fiók a második sorban, mert egy ügyfélnek több fiókja
      //    van), alatta a KÖZÖS „Részletek" kibontó. A fejléc a SORT nevezi meg, nem a mögötte álló
      //    dokumentumot — ugyanaz a nyelv, mint a Kiadások fülön.
      id: 'client',
      header: t('revenueForecast.columns.partner'),
      cardLabel: t('revenueForecast.columns.partner'),
      card: 'title',
      width: 'minmax(0,2.2fr)',
      multiline: true,
      cell: (r) => (
        <div className="min-w-0 space-y-1">
          <Text variant="itemTitle" as="div" className="truncate">
            {r.clientName ?? t('revenueForecast.unclassified')}
          </Text>
          {r.accountName !== null && (
            <Text tone="tertiary" as="div" className="truncate">
              {r.accountName}
            </Text>
          )}
          <DetailsExpander rows={detailRows(r)} />
        </div>
      ),
      cardExtraItems: (r) => detailRows(r),
    },
    {
      // 2. KATEGÓRIA — a szolgáltatás-katalógus FA-ága (`főkategória › alkategória`), ugyanabban az alakban,
      //    ahogy a Kiadások fülön a költség-kategória.
      //
      //    OLVASHATÓ, NEM szerkeszthető, és ez tudatos: a bevétel-sor kategóriája a HOZZÁRENDELÉS
      //    terv-pillanatképéből jön. Ha egy soron rossz, akkor a hozzárendelés rossz — ott javítva egyszerre
      //    helyreáll a terv, a tény és minden múltbeli meg jövőbeli hónap. Egy sor-szintű felülírás ezzel
      //    szemben visszakövethetetlenné tenné a kimutatást (ugyanaz a hónap más számot adna aszerint, ki
      //    mit írt felül). A kiadás-lapon azért MÁS a szabály, mert a NAV-ból érkező szállítói számlán nincs
      //    kategória — nincs miből származtatni, tehát a döntést tárolni kell.
      id: 'category',
      header: t('revenueForecast.columns.category'),
      cardLabel: t('revenueForecast.columns.category'),
      width: 'minmax(0,1.2fr)',
      multiline: true,
      cell: (r) => <RevenueCategoryCell row={r} t={t} />,
    },
    {
      // 3. SZOLGÁLTATÁS + csomag. MINDKÉT forráson tételenkénti: a tény-sor is EGY bizonylat-TÉTEL havi
      //    része, nem az egész számla — ezért egy több kategóriát tartalmazó számla helyesen bomlik szét.
      id: 'service',
      header: t('revenueForecast.columns.service'),
      cardLabel: t('revenueForecast.columns.service'),
      card: 'subtitle',
      width: 'minmax(0,1.6fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <div className="truncate">
            {r.serviceName ?? (
              <Text tone="tertiary" as="span">
                {t('revenueForecast.unclassifiedItem')}
              </Text>
            )}
          </div>
          {r.planName !== null && (
            <Text tone="tertiary" as="div" className="truncate">
              {r.planName}
            </Text>
          )}
        </div>
      ),
    },
    {
      // 4. ÖSSZEG — nettó/áfa/bruttó hármas, a SZÁMLA- és a KIADÁS-lista alakjával bitre azonosan.
      //    Mindhárom szám a HÓNAPRA JUTÓ rész (eredményszemlélet), és a terv-soron sem becslés: a
      //    terv-pillanatkép áfakulcsából számol, abból, amiből a havi generátor is.
      id: 'amount',
      header: t('revenueForecast.columns.amount'),
      cardLabel: t('revenueForecast.columns.amount'),
      width: 'minmax(0,1.4fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (r) => <RevenueAmountCell row={r} locale={locale} t={t} />,
    },
  ];

  const toolbar = (
    <ListToolbar
      // A HÓNAP mindig látható a sáv elején — mindhárom pénzügy-fülön egyformán.
      leading={<MonthStepper month={month} onMonthChange={onMonthChange} />}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: t('common.searchPlaceholder'),
      }}
      filterItems={filterItems}
      meta={
        infinite.isFetching && !infinite.isFetchingNextPage
          ? t('revenueForecast.list.loading')
          : !isError && body
            ? t('revenueForecast.list.countOf', { shown: rows.length, total: body.total })
            : ''
      }
    />
  );

  const content = (
    <div className="flex h-full min-h-0 flex-col gap-3 px-4 pb-2">
      {/* AZ ÖSSZEGZŐ: KÉT kártya — balra a pénz, jobbra az állapot. A váz a KÖZÖS `SummaryCards`, tehát a
          Kiadások fül csíkjával BITRE azonos: a Pénzügy lap két füle nem beszélhet két nyelvet. */}
      {body !== undefined && (
        <SummaryCards>
          <SummaryCard title={t('revenueForecast.list.monthTotal')}>
            {/* EGY hasáb, nem devizánkénti bontás: a lista összege MINDIG HUF (a deviza-tételek az MNB
                előző-hó-végi árfolyamán számolnak) — ezért a kiadás-lap deviza-oszlopai itt fogalmilag
                nincsenek. A darabszám a szám ALATT, ugyanabban a ritmusban, mint ott a bruttó + darab. */}
            <div className="min-w-0">
              <Text variant="secondary" as="div" className="text-xs">
                {t('revenueForecast.list.netTotal')}
              </Text>
              <div className="text-foreground truncate text-lg font-semibold tabular-nums">
                {formatMoney(body.totalAmount, 'HUF', locale)}
              </div>
              <Text tone="tertiary" as="div" className="truncate text-xs tabular-nums">
                {t('revenueForecast.list.itemCount', { count: body.total })}
              </Text>
            </div>
          </SummaryCard>

          <SummaryCard title={t('revenueForecast.list.statusTitle')}>
            <div className="space-y-2">
              {/* A FORRÁS a lap legfontosabb állapot-ténye: tény-adat vagy terv. MÚLT hónapon a terv TEENDŐ
                  (nem futott le a generálás), ezért ott warning-tónusú; jelen/jövő hónapon ez a normális
                  állapot, tehát semleges.

                  A `closed` jelzőt SZÁNDÉKOSAN NEM írjuk ki külön sorként: az nem azt jelenti, hogy „vége a
                  hónapnak", hanem hogy VAN rá bizonylat-adat — a lista viszont csak MÚLT hónapra vált
                  tény-adatra (`targetMonth < nowMonth && closed`). A FOLYÓ hónapon tehát a „terv" + „lezárult"
                  páros egyszerre igaz és olvasóként ellentmondásos; amit a felhasználónak tudnia kell (mit lát
                  és miért), azt a Forrás sora önmagában kimondja. */}
              <SummaryMetaRow
                label={t('revenueForecast.list.sourceLabel')}
                value={t(`revenueForecast.list.sourceShort.${body.source}`)}
                tone={
                  body.source === 'plan' && month < currentMonthKey() && !beforeHorizon
                    ? 'warning'
                    : 'default'
                }
              />
              {/* A KÉT ADAT-FENNTARTÁS csak akkor áll ki, ha fennáll — és warning-tónussal, mert mindkettő a
                  MUTATOTT SZÁMOT érinti. A bővebb magyarázatuk a lenti figyelem-sávokban marad (egy sor
                  címke–érték nem fér el egy mondatnyi indoklással). */}
              {body.usedFallbackRate && (
                <SummaryMetaRow
                  label={t('revenueForecast.list.rateLabel')}
                  value={t('revenueForecast.list.rateFallback')}
                  tone="warning"
                />
              )}
              {body.truncated && (
                <SummaryMetaRow
                  label={t('revenueForecast.list.listLabel')}
                  value={t('revenueForecast.list.listTruncated')}
                  tone="warning"
                />
              )}
            </div>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* A TERV-re esés MÚLT hónapon TEENDŐ-jelzés: a hónapra még nem futott le a generálás. */}
      {/* A horizont ELŐTTI hónap: nincs Atlas-számla — semleges magyarázat, nem „nem futott le a kör". */}
      {beforeHorizon && horizonFrom !== null && (
        <AttentionBanner
          tone="neutral"
          title={t('revenueForecast.list.beforeHorizon', {
            from: formatMonthLabel(horizonFrom, i18n.language),
          })}
        />
      )}
      {body?.source === 'plan' && month < currentMonthKey() && !beforeHorizon && (
        <AttentionBanner tone="warning" title={t('revenueForecast.list.notClosedYet')} />
      )}
      {/* LEZÁRT hónap + nem-alap státusz: a sorok a kiszámlázott tételek, a státusz rájuk NEM hat (a kártyával
          azonos szabály) — kimondjuk, hogy a szűrő-váltás után álló lista ne „beragadtnak" látsszon. */}
      {body?.source === 'actual' && isNonDefaultStatus(filters.statuses) && (
        <AttentionBanner tone="neutral" title={t('revenueForecast.list.statusNoEffectOnActual')} />
      )}
      {body?.usedFallbackRate === true && (
        <AttentionBanner tone="warning" title={t('revenueForecast.fallbackRateNote')} />
      )}
      {body?.truncated === true && (
        <AttentionBanner tone="warning" title={t('revenueForecast.actual.truncated')} />
      )}

      <div className="flex min-h-0 flex-1 flex-col">
        <DataTable<RevenueRow>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isLoading={infinite.isLoading}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasNarrowing ? (
              <EmptyState
                title={t('revenueForecast.emptyTitle')}
                description={t('revenueForecast.emptyDesc')}
              />
            ) : (
              <EmptyState
                title={t('revenueForecast.emptyMonthTitle')}
                description={t('revenueForecast.emptyMonthDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
        />
      </div>
    </div>
  );

  return { toolbar, content };
}

/** A jelen hónap `YYYY-MM` alakban — a „még nem zárult le" figyelmeztetés kapuja. */
function currentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * A KATEGÓRIA cellája: `főkategória › alkategória` — a szolgáltatás-katalógus fa-ága, ugyanabban az alakban,
 * ahogy a Kiadások fülön a költség-kategória. Az alkategória a második sorban, halványan: a fő a rendező elv,
 * az al a pontosítás.
 *
 * Kategória NÉLKÜLI sor (kézi számla, már törölt hozzárendelés) → kimondott hiány, nem üres cella: az üres
 * hely azt sugallná, hogy elfelejtettük kiírni.
 */
function RevenueCategoryCell({ row, t }: { row: RevenueRow; t: TFunction }): JSX.Element {
  if (row.mainCategory === null) {
    return (
      <Text tone="tertiary" as="span" className="truncate">
        {t('revenueForecast.unclassified')}
      </Text>
    );
  }
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="truncate">
        {t(`revenueForecast.mainCategory.${row.mainCategory}`, { defaultValue: row.mainCategory })}
      </div>
      {row.subCategory !== null && (
        <Text tone="tertiary" as="div" className="truncate">
          {t(`revenueForecast.subCategory.${row.subCategory}`, { defaultValue: row.subCategory })}
        </Text>
      )}
    </div>
  );
}

/**
 * Az ÖSSZEG cellája: `nettó · áfa · bruttó` — a számla- és a kiadás-lista blokkjával bitre azonos alak
 * (címke–érték párok kis betűvel, a számok jobbra egy vonalban, a bruttó előtt hajszálvonal).
 *
 * MINDHÁROM szám a HÓNAPRA JUTÓ rész (eredményszemlélet): egy negyedéves tétel három hónapon át a harmadával
 * szerepel. A TERV-soron sem becslés az áfa: a terv-pillanatkép áfakulcsából számol, pontosan abból a
 * mezőből, amiből a havi generátor is ki fogja állítani (`invoice-generation.service.ts`).
 */
function RevenueAmountCell({
  row,
  locale,
  t,
}: {
  row: RevenueRow;
  locale: string;
  t: TFunction;
}): JSX.Element {
  return (
    <div className="flex min-w-0 max-w-[13rem] flex-col gap-0.5 text-xs">
      <div className="flex justify-between gap-3">
        <span>{t('revenueForecast.amountNet')}</span>
        <span className="text-foreground whitespace-nowrap font-medium tabular-nums">
          {formatMoney(row.amount, 'HUF', locale)}
        </span>
      </div>
      <div className="flex justify-between gap-3">
        <span>{t('revenueForecast.amountVat')}</span>
        <span className="whitespace-nowrap tabular-nums">
          {formatMoney(row.vatAmount, 'HUF', locale)}
        </span>
      </div>
      <Divider tone="hairline" inset={false} spacing="none" className="my-0.5" />
      <div className="flex justify-between gap-3">
        <span>{t('revenueForecast.amountGross')}</span>
        <span className="whitespace-nowrap tabular-nums">
          {formatMoney(row.grossAmount, 'HUF', locale)}
        </span>
      </div>
    </div>
  );
}
